import { FieldTypeAsset, FieldTypeLink } from '@allesauto/utils/consts';
import {
  getUrlFromStoryblokLink,
  parseSlashToURL,
} from '@allesauto/utils/funcs';
import { sbEditable } from '@storyblok/storyblok-editable';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React from 'react';

import './ImageStage.css';

import Button from '../button';
import NextImage from '../nextImage';

export interface ImageStageProps {
  headline: string;
  text: string;
  imageLink: FieldTypeAsset;
  imageAlt?: string;
  darkBg?: boolean;
  buttonLabel?: string;
  buttonLink?: FieldTypeLink;
  mailToEmail?: string;
  hyphenatedHeadline?: boolean;
  component: string;
  fontSize?: number;
  _uid: string;
  _editable?: string;
  mobileFontSize?: number;
}

const ImageStage: React.FC<ImageStageProps> = (props: ImageStageProps) => {
  const {
    headline,
    text,
    darkBg = false,
    imageLink,
    imageAlt = '',
    buttonLabel,
    buttonLink,
    mailToEmail,
    hyphenatedHeadline = false,
    fontSize,
    mobileFontSize = fontSize,
  } = props;
  const router = useRouter();

  const className = {
    container: clsx(
      darkBg && 'bg-purple80 monochrome:bg-black',
      !darkBg && 'bg-grey20',
      'grid grid-cols-24 grid-rows-[min-content_1fr] py-80px'
    ),
    headline: clsx(
      'break-words font-headline overridable-font-size text-headline1 z-10 sm:col-end-23 sm:col-start-3 sm:my-30px md:my-40px lg:col-end-10 lg:col-start-2 lg:my-60px lg:row-end-2 lg:row-start-1 xl:text-title ',
      darkBg && 'text-green60 monochrome:text-white',
      !darkBg && 'text-purple80 monochrome:text-grey90',
      hyphenatedHeadline && 'hyphens-auto'
    ),
    text: clsx(
      'sm:col-end-23 sm:col-start-3 lg:col-end-10 lg:col-start-2 lg:row-end-3 lg:row-start-2',
      darkBg && 'text-white'
    ),
    button: clsx('mt-40px'),
    image: clsx(
      'aspect-video relative sm:col-end-23 sm:col-start-3 lg:col-end-24 lg:col-start-12 lg:row-end-3 lg:row-start-1 lg:w-full '
    ),
    overlay: clsx(
      'absolute h-full pointer-events-none top-0 w-full',
      darkBg && 'dark-gradient',
      !darkBg && 'light-gradient'
    ),
  };

  const handleButtonClick = () => {
    if (buttonLink) {
      const url = getUrlFromStoryblokLink(buttonLink);
      const openTab = buttonLink.target === '_blank';

      if (url) {
        const parsedUrl = parseSlashToURL(url);
        openTab ? window.open(parsedUrl, '_blank') : router.push(parsedUrl);
      }
    }

    if (mailToEmail) {
      return (document.location = `mailto:${mailToEmail}`);
    }
  };

  const imageUrl = imageLink?.filename ?? null;

  return (
    <div {...sbEditable(props)} className={className.container}>
      <div className={className.image}>
        <NextImage
          useCustomLoader={false}
          src={imageUrl}
          alt={imageLink?.alt ? imageLink.alt : imageAlt}
          layout="fill"
          objectFit="cover"
          priority
        />

        <div className={className.overlay} />
      </div>

      {fontSize && (
        // set dynamic font size using style tag since tailwind does not allow dynamic classes
        <style>
          {`.overridable-font-size { font-size: ${fontSize}rem }
          @media (max-width: 1200px) {
            .overridable-font-size { font-size: ${mobileFontSize}rem }
          }`}
        </style>
      )}

      <h1 className={className.headline}>{headline}</h1>

      <div className={className.text}>
        <p>{text}</p>

        {buttonLabel && (
          <div className={className.button}>
            <Button
              text={buttonLabel}
              type="button"
              whitelabel={true}
              onClick={handleButtonClick}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageStage;
